import { CloseOutlined } from '@ant-design/icons';
import React, { FC, useEffect, useMemo, useRef } from 'react';
import { User } from '../../../model/AccountModel';
import { PhotoCallUp } from '../../../model/PhotoCallUpModel';
import { canvasContainerId, canvasMainContainerId } from '../../common';
import { CardData } from '../../photocallup/CardData/CardData';
import styles from './graphicPhotoCallUpCard.module.scss';

const user: User = getUser();
const { photoCallUpBasicView } = user;

type Props = {
	arrowId: string;
	closeCard: (doorId: number) => void;
	doorNode: HTMLElement;
	index: number;
	photoCallUp: PhotoCallUp;
};

const GraphicPhotoCallUpCard: FC<Props> = ({ arrowId, closeCard, doorNode, index, photoCallUp }) => {
	const arrowRef = useRef<HTMLElement>();
	const canvasMainContainerRef = useRef<HTMLElement>();
	const cardContainerRef = useRef<HTMLDivElement>();
	const cardRef = useRef<HTMLElement>();
	const messagesContainerRef = useRef<HTMLElement>();

	const mutationObserverCallback = (mutationList: MutationRecord[]) => {
		for (const mutation of mutationList) {
			if (mutation.type === 'attributes') {
				if (mutation.attributeName === 'viewBox' && arrowRef.current && cardRef.current) {
					const doorNodePosition = doorNode.getBoundingClientRect();
					const cardParentNodePosition = cardRef.current.getBoundingClientRect();
					const canvasMainContainerNodePosition = canvasMainContainerRef.current.getBoundingClientRect();

					setCardPosition(canvasMainContainerNodePosition, cardParentNodePosition, doorNodePosition);
				}
			}
		}
	};

	const mutationObserver = new MutationObserver(mutationObserverCallback);

	const resizeObserver = new ResizeObserver(entries => {
		if (canvasMainContainerRef.current && cardRef.current && messagesContainerRef.current && entries?.length > 0) {
			const doorNodePosition = doorNode.getBoundingClientRect();
			const cardParentNodePosition = cardRef.current.getBoundingClientRect();
			const canvasMainContainerNodePosition = canvasMainContainerRef.current.getBoundingClientRect();

			setCardPosition(canvasMainContainerNodePosition, cardParentNodePosition, doorNodePosition);
		}
	});

	const setCardPosition = (canvasMainContainerNodePosition: DOMRect, cardParentNodePosition: DOMRect, doorNodePosition: DOMRect) => {
		arrowRef.current.style.borderBottom = 'none';
		arrowRef.current.style.borderLeft = 'none';
		arrowRef.current.style.borderRight = 'none';
		arrowRef.current.style.borderTop = 'none';

		let top = Math.ceil(doorNodePosition.top + doorNodePosition.height / 2 - cardParentNodePosition.height / 2);
		let left = Math.ceil(doorNodePosition.right + 17);

		if (top < canvasMainContainerNodePosition.top + 5) {
			top = canvasMainContainerNodePosition.top + 13;
		}

		if (top + cardParentNodePosition.height > canvasMainContainerNodePosition.bottom) {
			top = Math.ceil(canvasMainContainerNodePosition.bottom - cardParentNodePosition.height - 8);
		}

		arrowRef.current.style.borderBottom = 'none';
		arrowRef.current.style.borderLeft = 'none';
		arrowRef.current.style.borderRight = 'none';
		arrowRef.current.style.borderTop = 'none';
		let arrowTop = Math.ceil(doorNodePosition.top + doorNodePosition.height / 2 - 15);

		if (arrowTop < cardParentNodePosition.top + 12) {
			arrowTop = cardParentNodePosition.top + 12;
		}

		if (arrowTop + 30 > cardParentNodePosition.bottom - 20) {
			arrowTop = cardParentNodePosition.bottom - 20 - 30;
		}

		let topOrBottom: boolean = false;

		if (doorNodePosition.bottom < canvasMainContainerNodePosition.top - 10 || doorNodePosition.top > canvasMainContainerNodePosition.bottom + 10) {
			topOrBottom = true;
			if (doorNodePosition.bottom < canvasMainContainerNodePosition.top - 10) {
				arrowRef.current.style.borderLeft = '15px solid transparent';
				arrowRef.current.style.borderBottom = '15px solid rgba(22, 28, 46, 0.51)';
				arrowRef.current.style.borderRight = '15px solid transparent';
				arrowTop = top - 15;
			} else {
				top = top - 20;
				arrowRef.current.style.borderLeft = '15px solid transparent';
				arrowRef.current.style.borderTop = '15px solid rgba(22, 28, 46, 0.51)';
				arrowRef.current.style.borderRight = '15px solid transparent';
				arrowTop = top + cardParentNodePosition.height;
			}
			arrowRef.current.style.left = `${cardParentNodePosition.left + cardParentNodePosition.width / 2 - 15}px`;
		} else {
			if (left + cardParentNodePosition.width + 16 > canvasMainContainerNodePosition.right - 5) {
				left = Math.ceil(doorNodePosition.left - 17 - cardParentNodePosition.width);
				arrowRef.current.style.borderBottom = '15px solid transparent';
				arrowRef.current.style.borderLeft = '15px solid rgba(22, 28, 46, 0.51)';
				arrowRef.current.style.borderTop = '15px solid transparent';
				arrowRef.current.style.left = `${Math.ceil(doorNodePosition.left - 17)}px`;
			} else {
				arrowRef.current.style.borderBottom = '15px solid transparent';
				arrowRef.current.style.borderRight = '15px solid rgba(22, 28, 46, 0.51)';
				arrowRef.current.style.borderTop = '15px solid transparent';
				arrowRef.current.style.left = `${Math.ceil(doorNodePosition.right + 2)}px`;
			}
		}

		if (left - 20 < canvasMainContainerNodePosition.left) {
			left = canvasMainContainerNodePosition.left + 20;

			if (!topOrBottom) {
				arrowRef.current.style.left = `${cardParentNodePosition.left - 15}px`;
			}
		}

		if (left + 20 + cardParentNodePosition.width > canvasMainContainerNodePosition.right) {
			left = canvasMainContainerNodePosition.right - 20 - cardParentNodePosition.width;
			if (!topOrBottom) {
				arrowRef.current.style.left = `${cardParentNodePosition.right}px`;
			}
		}

		arrowRef.current.style.top = `${arrowTop}px`;
		cardRef.current.style.display = 'block';
		cardRef.current.style.left = `${left}px`;
		cardRef.current.style.top = `${top}px`;
	};

	const handleClose = () => {
		closeCard(photoCallUp.DoorId);
	};

	const containerId: string = useMemo<string>(() => `photoCallUpCardContainer-${photoCallUp.DoorId}`, []);

	useEffect(() => {
		messagesContainerRef.current = document.querySelector('[class~=ant-message]');
		const canvasMainContainerNode: HTMLElement = document.getElementById(canvasMainContainerId);

		if (cardContainerRef.current) {
			const cardParentNode: HTMLElement = cardContainerRef.current.closest('[class~=ant-message-notice]');

			if (messagesContainerRef.current) {
				const arrow: HTMLElement = messagesContainerRef.current.querySelector(`[id=${arrowId}]`);
				if (!arrow) {
					arrowRef.current = document.createElement('div');
				} else {
					arrowRef.current = arrow;
				}
				arrowRef.current.style.position = 'absolute';
				arrowRef.current.id = arrowId;
				arrowRef.current.style.display = 'none';
				messagesContainerRef.current.appendChild(arrowRef.current);

				setTimeout(() => {
					arrowRef.current.style.display = 'block';
				}, 200);

				cardRef.current = cardParentNode;
				canvasMainContainerRef.current = canvasMainContainerNode;
				messagesContainerRef.current.style.zIndex = '100';
			}
		}

		mutationObserver.observe(document.getElementById(`${canvasContainerId}_svg`), { attributes: true });
		resizeObserver.observe(document.getElementById(containerId));

		return () => {
			resizeObserver.disconnect();
			mutationObserver.disconnect();
		};
	}, []);

	return (
		<div id={containerId} className={styles.photoCallUpCardContainer} ref={cardContainerRef}>
			<CloseOutlined className={styles.closeIcon} style={{ fontSize: '12px' }} onClick={handleClose} />
			<div className={styles.cardDataContainer}>
				<CardData
					key={photoCallUp.DoorId}
					index={index}
					card={photoCallUp}
					showDoorName={true}
					showDateTime={true}
					showBorderCard={false}
					photoCallUpBasicView={photoCallUpBasicView}
				/>
			</div>
		</div>
	);
};

export { GraphicPhotoCallUpCard };
